import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Globe, LayoutDashboard, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_alibtisam-portal/artifacts/dl2ej7f9_PNG-Logo_Al_Ibtisam-Hijau.png";

const NAV = [
  { to: "/", key: "nav.beranda", id: "beranda" },
  { to: "/alumni", key: "nav.alumni", id: "alumni" },
  { to: "/galeri", key: "nav.galeri", id: "galeri" },
  { to: "/ppdb", key: "nav.ppdb", id: "ppdb" },
  { to: "/galang-dana", key: "nav.galang_dana", id: "galang-dana" },
  { to: "/program", key: "nav.program", id: "program" },
  { to: "/profil", key: "nav.profil", id: "profil" },
  { to: "/artikel", key: "nav.artikel", id: "artikel" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const linkCls = ({ isActive }) =>
    `px-1 py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
      isActive ? "text-emerald-800 border-[#D4AF37]" : "text-slate-600 border-transparent hover:text-emerald-800"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-emerald-900/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <img src={LOGO_URL} alt="Al Ibtisam" className="h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5" data-testid="main-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.id}`}>
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "id" ? "en" : "id")}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-full border border-emerald-800/20 px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-50 transition-colors duration-200"
          >
            <Globe className="h-3.5 w-3.5" /> {lang === "id" ? "ID" : "EN"}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full border border-emerald-800/20 pl-1 pr-2.5 py-1 hover:bg-emerald-50 transition-colors duration-200">
                  <span className="h-7 w-7 rounded-full bg-emerald-800 text-white text-xs font-bold flex items-center justify-center">{initials}</span>
                  <span className="hidden sm:block text-sm font-medium text-emerald-950 max-w-[110px] truncate">{user.name}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {t("auth.dasbor")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t("auth.keluar")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/masuk")}
              data-testid="login-button"
              className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white px-5"
            >
              {t("auth.masuk")}
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 text-emerald-900" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.id}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? "bg-emerald-800 text-white" : "text-slate-700 hover:bg-emerald-50"
                      }`
                    }
                  >
                    {t(n.key)}
                  </NavLink>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
